<template>
  <nav v-if="total > 0" class="platform-pagination studio-pagination" aria-label="分页导航">
    <!-- 左侧总数统计与每页条数选择 -->
    <div class="pagination-left pagination-summary">
      <span class="pagination-total total-text">
        共 <span class="count-badge">{{ total.toLocaleString() }}</span>
        <span class="item-name-text">{{ formattedItemName }}</span>
      </span>
      <div v-if="showSizeChanger" class="pagination-size-wrapper size-select-box">
        <select
          :value="pageSize"
          class="pagination-size-select size-select"
          aria-label="每页显示条数"
          @change="onPageSizeChange($event)"
        >
          <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">
            {{ opt }} 条/页
          </option>
        </select>
        <ChevronDown :size="13" class="select-chevron" />
      </div>
    </div>

    <!-- 右侧页码控制区 -->
    <div class="pagination-right pagination-controls">
      <!-- 上一页 -->
      <button
        type="button"
        class="btn-page-nav nav-btn prev-btn"
        :disabled="currentPage <= 1"
        title="上一页"
        aria-label="上一页"
        @click="onPageChange(currentPage - 1)"
      >
        <ChevronLeft :size="15" />
      </button>

      <!-- 页码列表 -->
      <div class="page-numbers-group page-list">
        <template v-for="(p, idx) in pageItems" :key="idx">
          <span v-if="p === '...'" class="page-ellipsis" aria-hidden="true">…</span>
          <button
            v-else
            type="button"
            class="btn-page-num page-number-btn"
            :class="{ active: currentPage === p }"
            :aria-current="currentPage === p ? 'page' : undefined"
            @click="onPageChange(p)"
          >
            {{ p }}
          </button>
        </template>
      </div>

      <!-- 下一页 -->
      <button
        type="button"
        class="btn-page-nav nav-btn next-btn"
        :disabled="currentPage >= totalPages"
        title="下一页"
        aria-label="下一页"
        @click="onPageChange(currentPage + 1)"
      >
        <ChevronRight :size="15" />
      </button>

      <!-- 快速跳页 -->
      <div v-if="showJumper && totalPages > 1" class="page-jumper">
        <span class="jumper-label">前往</span>
        <input
          v-model.trim="jumpInput"
          type="number"
          min="1"
          :max="totalPages"
          class="jumper-input"
          aria-label="跳转页码"
          placeholder=""
          @keydown.enter="handleJump"
        />
        <span class="jumper-label">页</span>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { ChevronLeft, ChevronRight, ChevronDown } from '@lucide/vue';

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  total: {
    type: Number,
    default: 0,
  },
  itemName: {
    type: String,
    default: '条',
  },
  pageSizeOptions: {
    type: Array,
    default: () => [10, 20, 50, 100],
  },
  showSizeChanger: {
    type: Boolean,
    default: true,
  },
  showJumper: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:currentPage', 'update:pageSize', 'change']);

const jumpInput = ref('');

const formattedItemName = computed(() => {
  const name = String(props.itemName || '').trim();
  if (!name || name === '条' || name === '条数据') return ' 条';
  if (name.startsWith('条') || name.startsWith('个') || name.startsWith('项')) {
    return ` ${name}`;
  }
  return ` ${name}`;
});

const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));

const pageItems = computed(() => {
  const totalP = totalPages.value;
  const current = props.currentPage;
  if (totalP <= 7) {
    return Array.from({ length: totalP }, (_, i) => i + 1);
  }
  const pages = [1];
  if (current > 3) pages.push('...');
  const start = Math.max(2, current - 1);
  const end = Math.min(totalP - 1, current + 1);
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (current < totalP - 2) pages.push('...');
  pages.push(totalP);
  return pages;
});

function onPageChange(page) {
  if (typeof page !== 'number') return;
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('update:currentPage', page);
    emit('change', { page, pageSize: props.pageSize });
    jumpInput.value = '';
  }
}

function onPageSizeChange(e) {
  const nextSize = Number(e.target.value);
  if (nextSize && nextSize !== props.pageSize) {
    emit('update:pageSize', nextSize);
    emit('update:currentPage', 1);
    emit('change', { page: 1, pageSize: nextSize });
    jumpInput.value = '';
  }
}

function handleJump() {
  const p = parseInt(jumpInput.value, 10);
  if (!Number.isNaN(p) && p >= 1 && p <= totalPages.value) {
    onPageChange(p);
  }
}

watch(() => props.currentPage, () => {
  jumpInput.value = '';
});
</script>

<style scoped>
.platform-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  margin-top: 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border: 1px solid rgba(226, 232, 240, 0.85);
  box-shadow:
    0 4px 18px -2px rgba(15, 23, 42, 0.04),
    0 1px 3px rgba(15, 23, 42, 0.02);
  flex-wrap: wrap;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.platform-pagination:hover {
  border-color: rgba(203, 213, 225, 0.95);
  box-shadow:
    0 6px 22px -2px rgba(15, 23, 42, 0.06),
    0 2px 5px rgba(15, 23, 42, 0.03);
}

/* 左侧统计与条数 */
.pagination-summary {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}

.total-text {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  background: #f1f5f9;
  border: 1px solid rgba(203, 213, 225, 0.6);
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.item-name-text {
  color: #64748b;
}

/* 条数下拉框 */
.size-select-box {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.size-select {
  height: 32px;
  padding: 0 28px 0 11px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  color: #334155;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);
}

.size-select:hover {
  border-color: #cbd5e1;
  background-color: #ffffff;
  color: #0f172a;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.04);
}

.size-select:focus {
  border-color: #7c3aed;
  background-color: #ffffff;
  color: #0f172a;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}

.select-chevron {
  position: absolute;
  right: 9px;
  pointer-events: none;
  color: #64748b;
  transition: transform 0.2s ease;
}

.size-select-box:hover .select-chevron {
  color: #0f172a;
}

/* 右侧控制器组 */
.pagination-controls {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

/* 上下页箭头按钮 */
.nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.nav-btn:hover:not(:disabled) {
  border-color: #7c3aed;
  color: #7c3aed;
  background: #f5f3ff;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(124, 58, 237, 0.16);
}

.nav-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(124, 58, 237, 0.12);
}

.nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  background: #f8fafc;
  border-color: #f1f5f9;
  color: #94a3b8;
  box-shadow: none;
  transform: none;
}

/* 页码按钮群 */
.page-list {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.page-number-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 7px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
}

.page-number-btn:hover:not(.active) {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #e2e8f0;
  transform: translateY(-1px);
}

.page-number-btn:active:not(.active) {
  transform: translateY(0);
}

.page-number-btn.active {
  background: linear-gradient(135deg, #7c3aed 0%, #9333ea 100%);
  color: #ffffff;
  font-weight: 700;
  border-color: rgba(0, 168, 68, 0.9);
  box-shadow:
    0 3px 10px rgba(124, 58, 237, 0.28),
    inset 0 1px 1px rgba(255, 255, 255, 0.25);
  cursor: default;
}

.page-ellipsis {
  min-width: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  user-select: none;
}

/* 前往页码跳转 */
.page-jumper {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: border-color 0.2s ease;
}

.page-jumper:focus-within {
  border-color: #7c3aed;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
}

.jumper-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  user-select: none;
}

.jumper-input {
  width: 38px;
  height: 26px;
  padding: 0 4px;
  text-align: center;
  border-radius: 5px;
  border: 1px solid transparent;
  background: #ffffff;
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  outline: none;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.jumper-input:hover {
  border-color: #cbd5e1;
}

.jumper-input:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.15);
}

/* 隐藏原生 number input 的上下调节微调箭头 */
.jumper-input::-webkit-outer-spin-button,
.jumper-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.jumper-input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

@media (max-width: 640px) {
  .platform-pagination {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 10px 14px;
  }
  .pagination-summary,
  .pagination-controls {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
